
import { defineComponent } from 'vue';
import api from '@/api/my/templates';
import utils from '@/utils';
import { IHost, ISite } from '@/types/models';
import agentSites from '@/api/agent/sites';
import agentAddSite from '@/api/agent/addSite';
import agentProcess from '@/api/agent/process';
import { IElForm } from '@/types/element';

interface IForm {
  hostId: number;
  siteName: string;
  root: boolean;
  siteDir: string;
  isParent: boolean;
  parentId: number;
}

interface IState {
  pageType: string;
  hosts: IHost[];
  host: IHost;
  form: IForm;
  rootExists: boolean;
  sites: ISite[];
  guid: string;
  total: number;
  current: number;
  message: string;
  success: boolean;
  errorMessage: string;
  site: ISite;
}

export default defineComponent({
  name: 'Domains',
  emits: ['close'],
  props: {
    userName: {
      type: String,
      required: true,
    },
    name: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      pageType: 'hosts',
      hosts: [],
      host: {} as IHost,
      form: {} as IForm,
      rootExists: false,
      sites: [],
      guid: '',
      total: 1,
      current: 0,
      message: '',
      success: false,
      errorMessage: '',
      site: {} as ISite,
    } as IState;
  },
  methods: {
    apiGet() {
      this.pageType = 'hosts';
      this.hosts = [];
      this.host = {} as IHost;
      this.form = {} as IForm;
      this.rootExists = false;
      this.sites = [];
      this.guid = '';
      this.total = 1;
      this.current = 0;
      this.message = '';
      this.success = false;
      this.errorMessage = '';
      this.site = {} as ISite;

      api.getHosts((res) => {
        if (!res) return;
        this.hosts = res.hosts;
        this.form = {} as IForm;
        this.sites = [];
      });
    },

    getUniqueName(host?: IHost) {
      return utils.getHostUniqueName(host || this.host);
    },

    getHostType(host: IHost) {
      return utils.getHostType(host.hostType);
    },

    formatDate(date: Date) {
      return utils.formatDate(date);
    },

    getUrl(hostName: string, nodeName: string, isHongKong: boolean): string {
      return utils.getHostUrl(hostName, nodeName, isHongKong);
    },

    getHostUrl(): string {
      return utils.getHostUrl(this.host.hostName, this.host.nodeName, this.host.isHongKong);
    },

    getSitUrl(site: ISite) {
      return utils.getSiteUrl(this.host, site);
    },

    btnHostClick(host: IHost) {
      this.host = host;
      agentSites.getSites(
        this.host.hostName,
        this.host.nodeName,
        this.host.isHongKong,
        {
          securityKey: this.host.securityKey,
        },
        (res) => {
          this.sites = res.sites;
          this.rootExists = res.rootSiteId > 0;
          this.form.root = !this.rootExists;
          this.form.isParent = false;
          this.form.parentId = 0;
          this.pageType = 'submit';
        },
      );
    },

    apiProcess() {
      if (this.success) return;
      this.pageType = 'process';

      agentProcess.process(
        this.host.hostName,
        this.host.nodeName,
        this.host.isHongKong,
        {
          securityKey: this.host.securityKey,
          guid: this.guid,
        },
        (res) => {
          if (this.success) return;

          this.total = res.total || 1;
          this.current = res.current;
          this.message = res.message;

          if (this.current > this.total) {
            this.current = this.total;
          }

          if (this.total > this.current) {
            setTimeout(() => {
              this.apiProcess();
            }, 3000);
          }
          utils.loading(false);
        },
      );
    },

    apiSubmit() {
      this.pageType = 'process';
      api.getTheme(
        {
          userName: this.userName,
          name: this.name,
        },
        (res) => {
          if (!res) return;
          this.guid = res.guid;

          const interval = setTimeout(() => {
            this.apiProcess();
          }, 3000);

          agentAddSite.addSite(
            this.host.hostName,
            this.host.nodeName,
            this.host.isHongKong,
            {
              securityKey: this.host.securityKey,
              siteName: this.form.siteName,
              root: this.form.root,
              parentId: this.form.isParent ? this.form.parentId : 0,
              siteDir: this.form.siteDir,
              themeDownloadUrl: res.themeDownloadUrl,
              guid: res.guid,
            },
            (res2) => {
              if (res2.site) {
                this.total = 1;
                this.current = 1;
                this.message = '站点创建成功！';
                this.success = true;
                this.pageType = 'success';
                this.site = res2.site;
              }
            },
            (errorMessage) => {
              clearTimeout(interval);
              this.pageType = 'submit';
              utils.loading(false);
              utils.error(errorMessage);
            },
          );
        },
      );
    },

    btnCloseClick() {
      this.$emit('close');
    },

    btnSubmitClick() {
      (this.$refs.form as IElForm).validate((valid) => {
        if (valid) {
          this.apiSubmit();
        }
      });
    },
  },
  watch: {
    userName: {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.apiGet();
        }
      },
      deep: false,
    },
    name: {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.apiGet();
        }
      },
      deep: false,
    },
  },
  mounted() {
    this.apiGet();
  },
});
